import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import SidePanel from "./SidePanel";
import { useLiveProducts } from "@/lib/siteContent";
import { formatBRL } from "@/lib/format";

interface SearchPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchPanel({ open, onClose }: SearchPanelProps) {
  const [query, setQuery] = useState("");
  const products = useLiveProducts();

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return products.slice(0, 6);
    return products.filter((product) => {
      const colors = product.colors.map((color) => color.name.toLowerCase()).join(" ");
      return (
        product.name.toLowerCase().includes(term) ||
        product.category.toLowerCase().includes(term) ||
        product.categoryId.includes(term) ||
        product.code.toLowerCase().includes(term) ||
        colors.includes(term)
      );
    });
  }, [query]);

  return (
    <SidePanel
      open={open}
      title="O que você procura?"
      subtitle="Busque por nome, categoria, código ou cor."
      onClose={onClose}
    >
      <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-3">
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-search-line text-lg text-foreground-500" />
        </span>
        <input
          type="text"
          name="busca"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Sandália, tênis, bolsa..."
          className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
        />
      </div>

      <p className="mt-4 text-xs text-foreground-500">
        {query.trim() ? `${results.length} resultado(s) para "${query.trim()}"` : "Produtos em destaque"}      </p>

      <div className="mt-4 flex flex-col gap-3">
        {results.length === 0 ? (
          <p className="rounded-lg border border-background-200 bg-background-100 p-4 text-sm text-foreground-600">
            Não encontramos esse produto. Tente outro nome, categoria ou cor.
          </p>
        ) : (
          results.map((product) => (
            <Link
              key={product.id}
              to={`/produto/${product.id}`}
              onClick={onClose}
              className="flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:bg-background-100"
            >
              <div className="h-16 w-14 shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={product.colors[0].image}
                  alt={product.name}
                  title={`${product.name} — Auréa`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-lg text-foreground-900">{product.name}</p>
                <p className="text-xs text-foreground-500">{product.category}</p>
              </div>
              <span className="shrink-0 text-sm font-medium text-primary-700">
                {formatBRL(product.promoPrice ?? product.price)}
              </span>
            </Link>
          ))
        )}
      </div>
    </SidePanel>
  );
}